import { Search } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import Pagination from "./Pagination";

/**
 * Generic data table component
 * columns: [{ key, label, render?, align?, width?, mobileHide?, mobileLabel? }]
 * actions: (row) => ReactNode
 *
 * Behaviour:
 *  - Newest-first ordering: rows are sorted by `created_at` desc by default so
 *    the newest entries appear at the top. Pass `sortNewestFirst={false}` to
 *    keep the parent's order.
 *  - Pagination: 12 rows per page by default. Pass `pageSize` to override or
 *    `paginated={false}` to show every row on a single page.
 *  - Search: client-side substring match across every column unless `onSearch`
 *    is provided (which delegates filtering to the parent).
 *
 * Responsive: on screens < md (768px), each row is rendered as a stacked card
 * with label: value pairs instead of a cramped horizontal table.
 * Pass `mobileHide: true` on a column to omit it from the mobile card view.
 */
export default function DataTable({
  columns,
  rows,
  loading,
  searchable = true,
  onSearch,
  actions,
  emptyMessage = "No records yet.",
  testId = "data-table",
  initialSearch = "",
  pageSize = 12,
  paginated = true,
  sortNewestFirst = true,
}) {
  const [q, setQ] = useState(initialSearch || "");
  const [page, setPage] = useState(1);

  // Update filter when an initial search is provided after first mount
  // (e.g., top-bar smart search seeds the query via sessionStorage).
  useEffect(() => {
    if (initialSearch) {
      setQ(initialSearch);
      if (onSearch) onSearch(initialSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  // Newest-first ordering — applied before search/pagination
  const sortedRows = useMemo(() => {
    if (!sortNewestFirst) return rows || [];
    const arr = Array.isArray(rows) ? [...rows] : [];
    arr.sort((a, b) => {
      const av = String(a?.created_at || a?.joined || a?.updated_at || "");
      const bv = String(b?.created_at || b?.joined || b?.updated_at || "");
      return bv.localeCompare(av);
    });
    return arr;
  }, [rows, sortNewestFirst]);

  const filtered = useMemo(() => {
    if (!q || onSearch) return sortedRows;
    const lc = q.toLowerCase();
    return sortedRows.filter((r) =>
      columns.some((c) => {
        // Per-column extra search keys (string array) — used when a single
        // visual column aggregates multiple row fields (e.g. Player column
        // showing username + player_id + full_name).
        if (Array.isArray(c.searchKeys)) {
          if (c.searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(lc))) return true;
        }
        return String(r[c.key] ?? "").toLowerCase().includes(lc);
      })
    );
  }, [q, sortedRows, columns, onSearch]);

  // Reset to page 1 whenever the filtered list size changes (search or new data)
  useEffect(() => {
    setPage(1);
  }, [q, filtered.length]);

  const total = filtered.length;
  const visible = useMemo(() => {
    if (!paginated) return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize, paginated]);

  const mobileCols = columns.filter((c) => !c.mobileHide);

  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid={testId}>
      {searchable && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2 border border-gray-200 rounded-sm px-3 py-1.5 w-full sm:max-w-sm bg-[#FAFAFA]">
            <Search size={14} className="text-gray-400" />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                if (onSearch) onSearch(e.target.value);
              }}
              placeholder="Search..."
              className="bg-transparent outline-none text-sm flex-1 placeholder:text-gray-400"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="text-xs text-gray-500 tabular shrink-0">
            {total} result{total === 1 ? "" : "s"}
          </div>
        </div>
      )}

      {/* Desktop / tablet table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200 px-4 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}
                  style={c.width ? { width: c.width } : undefined}
                >
                  {c.label}
                </th>
              ))}
              {actions && <th className="bg-gray-50 border-b border-gray-200 px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center text-sm text-gray-500 py-10">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && total === 0 && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center text-sm text-gray-500 py-10">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading &&
              visible.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  className="border-b border-gray-100 row-hover transition-colors"
                  data-testid={`${testId}-row-${idx}`}
                >
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-3 text-sm text-gray-700 whitespace-nowrap ${c.align === "right" ? "text-right tabular" : ""}`}
                    >
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                  {actions && <td className="px-4 py-3 text-right">{actions(row)}</td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="md:hidden">
        {loading && (
          <div className="text-center text-sm text-gray-500 py-10">Loading...</div>
        )}
        {!loading && total === 0 && (
          <div className="text-center text-sm text-gray-500 py-10">{emptyMessage}</div>
        )}
        {!loading && total > 0 && (
          <ul className="divide-y divide-gray-100">
            {visible.map((row, idx) => (
              <li
                key={row.id || idx}
                className="p-4 space-y-2"
                data-testid={`${testId}-card-${idx}`}
              >
                {mobileCols.map((c) => {
                  const value = c.render ? c.render(row) : row[c.key];
                  if (value === undefined || value === null || value === "") return null;
                  return (
                    <div key={c.key} className="flex items-start justify-between gap-3 text-sm">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 shrink-0 pt-0.5 min-w-[96px]">
                        {c.mobileLabel || c.label}
                      </div>
                      <div className={`text-gray-800 text-right break-all ${c.align === "right" ? "tabular" : ""}`}>
                        {value}
                      </div>
                    </div>
                  );
                })}
                {actions && (
                  <div className="flex items-center justify-end flex-wrap gap-2 pt-2 border-t border-gray-100">
                    {actions(row)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {paginated && !loading && total > 0 && (
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={setPage}
          testIdPrefix={`${testId}-pg`}
        />
      )}
    </div>
  );
}
